import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList,
  PlusCircle,
  Pencil,
  Eye,
  Trash2,
  Download,
  Search,
} from 'lucide-react';

const ViewEntry = ({ data, onDelete, onEdit, onView }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = data.filter(entry =>
        entry.patientName.toLowerCase().includes(lowercasedQuery) ||
        (entry.mobile && entry.mobile.toLowerCase().includes(lowercasedQuery)) ||
        (entry.diagnosis && entry.diagnosis.toLowerCase().includes(lowercasedQuery)) ||
        (entry.city && entry.city.toLowerCase().includes(lowercasedQuery))
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [searchQuery, data]);

  const handleDownload = (entry) => {
    console.log('Downloading entry for:', entry.patientName);
    alert(`Downloading patient data for ${entry.patientName}...`);
  };

  const styles = `
    :root {
      --primary-bg-color: #eef2f8;
      --main-card-bg-color: #ffffff;
      --main-card-border-color: #e2e8f0;
      --main-card-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
      --header-bg: linear-gradient(180deg, #f0f4f9, #ffffff);
      --header-border-color: #cbd5e1;
      --header-title-color: #1f2937;
      --text-color: #4b5563;
      --accent-color: #0ea5e9;
      --button-gradient-start: #38bdf8;
      --button-gradient-end: #2dd4bf;
      --button-hover-gradient-start: #0ea5e9;
      --button-hover-gradient-end: #14b8a6;
      --table-header-bg: #f8fafc;
      --table-row-hover: #f1f5f9;
      --radius-md: 12px;
      --radius-sm: 8px;
    }

    body {
      font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
      margin: 0;
      padding: 0;
      color: var(--text-color);
    }

    .app-container {
      min-height: 100vh;
      background-color: var(--primary-bg-color);
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 2rem 1.5rem;
    }
    
    .main-card {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      background: var(--main-card-bg-color);
      border-radius: var(--radius-md);
      box-shadow: var(--main-card-shadow);
      border: 1px solid var(--main-card-border-color);
      overflow: hidden;
    }
    
    @media (min-width: 1024px) {
      .main-card {
        margin-left: 14rem;
      }
    }
    
    .header {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1.5rem 2rem;
      border-bottom: 2px solid var(--header-border-color);
      background: var(--header-bg);
    }
    
    @media (min-width: 640px) {
      .header {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }

    .header-content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }

    @media (min-width: 640px) {
      .header-content {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }

    .header-top {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    @media (min-width: 640px) {
      .header-top {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }

    .header-title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--header-title-color);
      margin: 0;
    }
    
    .header-title .icon {
      color: var(--accent-color);
    }
    
    .header-nav {
      display: flex;
      gap: 1rem;
      align-items: center;
      font-size: 0.95rem;
      color: var(--text-color);
    }

    .search-bar-container {
      position: relative;
      width: 100%;
      max-width: 300px;
    }

    @media (min-width: 640px) {
      .search-bar-container {
        max-width: 230px;
        order: -1; /* Place search bar before the button on desktop */
      }
    }
    
    .search-bar {
      width: 100%;
      padding: 0.75rem 1rem 0.75rem 2.5rem;
      border-radius: 8px;
      border: 1px solid var(--main-card-border-color);
      font-size: 1rem;
      transition: all 0.2s ease;
      outline: none;
      color: var(--text-color);
      background-color: #f8fafc;
    }

    .search-bar:focus {
      border-color: var(--accent-color);
      box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.2);
    }

    .search-icon {
      position: absolute;
      top: 50%;
      left: 0.75rem;
      transform: translateY(-50%);
      color: #94a3b8;
      pointer-events: none;
    }

    .header-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background-image: linear-gradient(90deg, var(--button-gradient-start), var(--button-gradient-end));
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      font-weight: 600;
      box-shadow: 0 4px 15px rgba(3, 105, 161, 0.15);
      transition: all 0.2s ease;
      text-decoration: none;
    }
    
    .header-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(3, 105, 161, 0.25);
    }
    
    .main-content {
      padding: 2rem;
    }
    
    .submitted-data-table-container {
      margin-top: 1rem;
    }
    
    .table-title {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      color: var(--header-title-color);
    }
    
    .table-wrapper {
      overflow-x: auto;
    }
    
    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.95rem;
    }

    .data-table th,
    .data-table td {
      padding: 1rem 0.25rem;
      text-align: left;
      border-bottom: 1px solid var(--main-card-border-color);
    }
    
    .data-table th {
      background-color: var(--table-header-bg);
      font-weight: 600;
      color: #374151;
    }
    
    .data-table tbody tr:hover {
      background-color: var(--table-row-hover);
    }
    
    .data-table tbody tr:last-child td {
      border-bottom: none;
    }
    
    .no-data-message {
      text-align: center;
      color: #6b7280;
      padding: 2.5rem;
      border: 1px dashed #d1d5db;
      border-radius: var(--radius-sm);
      background-color: var(--table-header-bg);
    }
    
    .action-cell {
      display: flex;
      gap: 0.5rem;
    }
    .action-btn {
      padding: 0.6rem;
      border-radius: var(--radius-sm);
      border: 1px solid #d1d5db;
      background-color: #f9fafb;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      color: inherit;
    }
    .action-btn:hover {
      background-color: #e5e7eb;
      transform: translateY(-1px);
    }
    .action-btn.edit { color: #2563eb; }
    .action-btn.view { color: #059669; }
    .action-btn.delete { color: #dc2626; }
    .action-btn.download { color: #f97316; }
    
    .action-btn span {
      display: none;
    }

    @media (max-width: 768px) {
      .table-wrapper {
        overflow-x: hidden;
      }
      .data-table {
        border: none;
        width: 100%;
      }
      .data-table thead {
        display: none;
      }
      .data-table tbody, .data-table tr, .data-table td {
        display: block;
        width: 100%;
      }
      .data-table tr {
        margin-bottom: 1.5rem;
        border: 1px solid var(--main-card-border-color);
        border-radius: var(--radius-sm);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
        background: #fff;
        padding: 1.25rem;
        transition: transform 0.2s ease;
      }
      .data-table tr:hover {
        transform: translateY(-2px);
      }
      .data-table td {
        text-align: right;
        position: relative;
        padding-left: 50%;
        border-bottom: 1px dashed #e6e9ef;
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
        font-size: 0.95rem;
      }
      .data-table td:last-child {
        border-bottom: none;
      }
      .data-table td::before {
        content: attr(data-label);
        position: absolute;
        left: 1.25rem;
        width: calc(50% - 1.25rem - 1rem);
        text-align: left;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: #4b5563;
      }

      .action-cell {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0;
        border-top: 1px solid var(--main-card-border-color);
      }

      .action-btn {
        padding: 0.75rem 1.25rem;
        border-radius: 9999px;
        border: 1px solid #d1d5db;
        background-color: #f9fafb;
        display: flex;
        justify-content: center;
        align-items: center;
        color: inherit;
        transition: all 0.2s ease;
      }
      
      .action-btn:hover {
        background-color: #e5e7eb;
        transform: translateY(-1px);
      }

      .action-btn span {
        display: none;
      }
      .action-btn svg {
        font-size: 1.25rem;
      }
      
      .action-btn.edit { color: #3b82f6; }
      .action-btn.edit:hover { color: #2563eb; }
      .action-btn.view { color: #10b981; }
      .action-btn.view:hover { color: #059669; }
      .action-btn.delete { color: #ef4444; }
      .action-btn.delete:hover { color: #dc2626; }
      .action-btn.download { color: #f97316; }
      .action-btn.download:hover { color: #ea580c; }
    }
  `;

  return (
    <div className="app-container">
      <style>{styles}</style>
      <div className="main-card">
        <header className="header">
          <div className="header-content">
            <h1 className="header-title">
              <ClipboardList className="icon" />
              Patient List
            </h1>
            <div className="header-nav">
              <div className="search-bar-container">
                <Search size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search patients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-bar"
                />
              </div>
              <Link to="/NewEntry">
                <button className="header-button" type="button">
                  <PlusCircle size={20} />
                  Add New Patient
                </button>
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="submitted-data-table-container">
            {filteredData.length === 0 ? (
              <p className="no-data-message">
                {searchQuery ? "No patients found matching your search." : "No patient data submitted yet. Please add a new entry."}
              </p>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Patient No.</th>
                      <th>Date</th>
                      <th>Patient Name</th>
                      <th>Reg. Mode</th>
                      <th>Mobile No.</th>
                      <th>Age</th>
                      <th>Diagnoses</th>
                      <th>City</th>
                      <th>State</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((entry, index) => (
                      <tr key={index}>
                        <td data-label="Patient No.">{index + 1}</td>
                        <td data-label="Date">{entry.entryDate}</td>
                        <td data-label="Patient Name">{entry.patientName}</td>
                        <td data-label="Reg. Mode">{entry.mode}</td>
                        <td data-label="Mobile No.">{entry.mobile}</td>
                        <td data-label="Age">{entry.age}</td>
                        <td data-label="Diagnoses">{entry.diagnosis}</td>
                        <td data-label="City">{entry.city}</td>
                        <td data-label="State">{entry.state}</td>
                        <td className="action-cell" data-label="ACTION">
                          <button onClick={() => onEdit(entry)} className="action-btn edit" title="Edit">
                            <Pencil size={18} />
                          </button>
                          <button onClick={() => onView(entry)} className="action-btn view" title="View">
                            <Eye size={18} />
                          </button>
                          <button onClick={() => onDelete(index)} className="action-btn delete" title="Delete">
                            <Trash2 size={18} />
                          </button>
                          <button onClick={() => handleDownload(entry)} className="action-btn download" title="Download">
                            <Download size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ViewEntry;